import {
  UpdateIconButton,
  Checkbox,
  ListMenu,
  pn,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@compass/core";

import { ListBeProperty } from "components";
import {
  ClassListDto,
  ClassListResource,
  Permission,
  PermissionAction,
  SortObject,
} from "generated/openApi";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import { ClassPageRoute } from "../ClassPage/ClassPage";
import { useClassListMenuItems } from "./useClassListMenuItems";

export const ClassTable = (props: {
  rootPermission: Permission;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  classes: ClassListDto[];
  resource: ClassListResource;
  searchFocused: boolean;
  sortObjects: SortObject[] | undefined;
  sort: (sortObjects: SortObject[]) => void;
  loadList: () => void;
  listTotal: number | undefined;
  selectId: (id: number) => void;
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  getIdsPromise: () => Promise<any>;
  onBulkEdit: () => void;
}) => {
  const navigate = useNavigate();
  const properties = props.resource.properties || [];
  const hookMenuItems = useClassListMenuItems({ permission: props.rootPermission, selectedIds: props.selectedIds });

  const menuItems = [
    {
      label: `Update (${props.selectedIds.length})`,
      action: props.onBulkEdit,
      disabled: props.selectedIds.length === 0,
    },
    ...hookMenuItems,
  ];

  return (
    <TableContainer ref={props.tableContainerRef}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell type="select">
              <ListMenu
                selectedIds={props.selectedIds}
                setSelectedIds={props.setSelectedIds}
                listTotal={props.listTotal}
                fetchFunction={() => props.getIdsPromise()}
                actionMenuItems={menuItems}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("id"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("name"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("identification"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("school"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("classType"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("classLevel"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("usedBy"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell>
              <ListBeProperty
                listProperty={properties.find((x) => x.propertyName === pn<ClassListDto>("isLocked"))}
                searchFocused={props.searchFocused}
                sortObjects={props.sortObjects}
                onChange={(sortObjects) => props.sort(sortObjects)}
              />
            </TableCell>
            <TableCell type="action" />
          </TableRow>
        </TableHead>

        <TableBody>
          {props.classes.map((classItem) => (
            <TableRow
              key={classItem.id}
              onClick={() =>
                navigate(
                  ClassPageRoute.getPath({
                    id: classItem.id,
                    crudMode: "view",
                    screenType: "dialog"
                  })
                )
              }
            >
              <TableCell type="select">
                <Checkbox
                  checked={props.selectedIds.includes(classItem.id)}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevents navigating when clicking the checkbox
                    props.selectId(classItem.id);
                  }}
                />
              </TableCell>
              <TableCell>{classItem.id}</TableCell>
              <TableCell>{classItem.name}</TableCell>
              <TableCell>{classItem.identification || "N/A"}</TableCell>
              <TableCell>{classItem.school}</TableCell>
              <TableCell>{classItem.classType}</TableCell>
              <TableCell>{classItem.classLevel}</TableCell>
              <TableCell>{classItem.usedBy}</TableCell>
              <TableCell>{classItem.isLocked ? "Yes" : "No"}</TableCell>
              <TableCell type="action">
                {isAuthorized(props.rootPermission, PermissionAction.Update) && (
                  <UpdateIconButton color="primary"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent row click
                      navigate(
                        ClassPageRoute.getPath({
                          id: classItem.id,
                          crudMode: "edit",
                          screenType: "dialog",
                        })
                      );
                    }}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
